import React, { useCallback, useEffect, useState } from 'react';
import { ImagePlus, Pencil, Plus, Trash2 } from 'lucide-react';
import {
  TaskyAlert,
  TaskyBadge,
  TaskyButton,
  TaskyDialog,
  TaskyIconButton,
  TaskyInput,
  TaskySelect,
  TaskySpinner,
  TaskySwitch,
  TaskyTabs,
  TaskyTextarea,
  useConfirm,
} from '../tasky';
import adminUgcPresetService, {
  type AdminUgcPreset,
  type UgcPresetInput,
  type UgcPresetKind,
  UGC_VOICES,
} from '../../services/api/adminUgcPresetService';

const KIND_TABS: { value: UgcPresetKind; label: string }[] = [
  { value: 'talent', label: '出演者' },
  { value: 'location', label: '場所' },
];

/** 種別ごとの入力例（出演者と場所で書く内容が違う） */
const PLACEHOLDERS: Record<UgcPresetKind, { name: string; description: string; promptHint: string }> = {
  talent: {
    name: '自然体の30代女性',
    description: '部屋で話す。親しみやすく、口コミに近い',
    promptHint: 'Japanese woman in her 30s, shoulder-length hair, beige knit',
  },
  location: {
    name: 'リビング・自然光',
    description: '生活感があり、いちばん口コミらしい',
    promptHint: 'Cozy Japanese living room, soft natural daylight from the window, plants and a wooden table',
  },
};

const EMPTY: UgcPresetInput = { name: '', description: '', prompt_hint: '', voice: null, image_key: null, sort_order: 0, is_active: true };

/**
 * 管理画面「UGC 出演者・場所」。チャットの UGC 風動画で選ぶ出演者（顔画像）と場所（写真）を登録する。
 * 顔画像・写真は絵コンテの参照画像になり、描写（英語推奨）は映像プロンプトに入る。全組織で共通。
 */
const UgcPresetPanel: React.FC = () => {
  const confirm = useConfirm();
  const [kind, setKind] = useState<UgcPresetKind>('talent');
  const [items, setItems] = useState<AdminUgcPreset[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<AdminUgcPreset | 'new' | null>(null);
  const [form, setForm] = useState<UgcPresetInput>(EMPTY);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      setItems(await adminUgcPresetService.list(kind));
    } catch {
      setError('読み込めませんでした。');
    } finally {
      setIsLoading(false);
    }
  }, [kind]);

  useEffect(() => {
    void load();
  }, [load]);

  const open = (item: AdminUgcPreset | 'new') => {
    setEditing(item);
    setForm(item === 'new' ? { ...EMPTY, kind } : { ...item });
    setImageUrl(item === 'new' ? null : item.image_url);
  };

  const upload = async (file: File | undefined) => {
    if (!file) return;
    setIsSaving(true);
    try {
      const result = await adminUgcPresetService.upload(file);
      setForm((prev) => ({ ...prev, image_key: result.key }));
      setImageUrl(result.url);
    } catch {
      setError('画像をアップロードできませんでした（JPEG / PNG / WebP・5MB まで）。');
    } finally {
      setIsSaving(false);
    }
  };

  const save = async () => {
    if (!form.name?.trim()) return;
    setIsSaving(true);
    setError(null);
    try {
      const payload: UgcPresetInput = {
        kind,
        name: form.name.trim(),
        description: form.description || null,
        prompt_hint: form.prompt_hint || null,
        image_key: form.image_key || null,
        voice: kind === 'talent' ? form.voice || null : null,
        sort_order: Number(form.sort_order) || 0,
        is_active: form.is_active ?? true,
      };
      if (editing === 'new') await adminUgcPresetService.create(payload);
      else if (editing) await adminUgcPresetService.update(editing.id, payload);
      setEditing(null);
      await load();
    } catch {
      setError('保存できませんでした。入力を確かめてください。');
    } finally {
      setIsSaving(false);
    }
  };

  const remove = async (item: AdminUgcPreset) => {
    if (!(await confirm({ title: `「${item.name}」を削除しますか？`, description: '作りかけの絵コンテは影響を受けません。', confirmText: '削除', tone: 'destructive' }))) return;
    try {
      await adminUgcPresetService.remove(item.id);
      await load();
    } catch {
      setError('削除できませんでした。');
    }
  };

  const toggle = async (item: AdminUgcPreset) => {
    try {
      const updated = await adminUgcPresetService.toggle(item.id);
      setItems((prev) => prev.map((row) => (row.id === updated.id ? updated : row)));
    } catch {
      setError('切り替えられませんでした。');
    }
  };

  return (
    <div className="flex flex-col gap-4 rounded-tasky-md border border-warm-border bg-white p-4 sm:p-6" data-testid="ugc-preset-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold text-dark">UGC 出演者・場所</h3>
          <p className="text-sm text-gray-500">チャットの UGC 風動画で選ぶ候補です。顔画像・写真は絵コンテの参照画像になります。</p>
        </div>
        <TaskyButton size="sm" onClick={() => open('new')}>
          <Plus aria-hidden />
          {kind === 'talent' ? '出演者を追加' : '場所を追加'}
        </TaskyButton>
      </div>

      <TaskyTabs items={KIND_TABS} value={kind} onChange={(value) => setKind(value as UgcPresetKind)} />

      {error && <TaskyAlert tone="error">{error}</TaskyAlert>}

      {isLoading ? (
        <TaskySpinner />
      ) : items.length === 0 ? (
        <p className="py-8 text-center text-sm text-gray-500">まだ登録がありません。</p>
      ) : (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
          {items.map((item) => (
            <div key={item.id} className="flex flex-col gap-2 rounded-tasky-md border border-warm-border p-2" data-testid="ugc-preset-item">
              <div className="aspect-[3/4] overflow-hidden rounded-tasky-sm bg-warm-surface">
                {item.image_url && <img src={item.image_url} alt="" className="h-full w-full object-cover" />}
              </div>
              <div className="flex items-start justify-between gap-1">
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-dark">{item.name}</p>
                  {item.description && <p className="line-clamp-2 text-xs text-gray-500">{item.description}</p>}
                </div>
                {!item.is_active && <TaskyBadge variant="secondary">停止中</TaskyBadge>}
              </div>
              <div className="flex items-center justify-between">
                <TaskySwitch checked={item.is_active} onChange={() => void toggle(item)} aria-label={`${item.name}を使う`} />
                <span className="flex gap-1">
                  <TaskyIconButton size="sm" variant="ghost" aria-label={`${item.name}を編集`} onClick={() => open(item)}>
                    <Pencil />
                  </TaskyIconButton>
                  <TaskyIconButton size="sm" variant="ghost" aria-label={`${item.name}を削除`} onClick={() => void remove(item)}>
                    <Trash2 />
                  </TaskyIconButton>
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      <TaskyDialog
        open={editing !== null}
        onClose={() => setEditing(null)}
        title={editing === 'new' ? (kind === 'talent' ? '出演者を追加' : '場所を追加') : '編集'}
        footer={
          <div className="flex justify-end gap-2">
            <TaskyButton variant="ghost" onClick={() => setEditing(null)}>
              やめる
            </TaskyButton>
            <TaskyButton onClick={() => void save()} disabled={isSaving || !form.name?.trim()}>
              保存
            </TaskyButton>
          </div>
        }
      >
        <div className="flex flex-col gap-3">
          <label className="flex flex-col gap-1 text-sm font-medium">
            名前
            <TaskyInput value={form.name ?? ''} maxLength={100} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={PLACEHOLDERS[kind].name} />
          </label>
          <label className="flex flex-col gap-1 text-sm font-medium">
            説明（選択肢の下に出る）
            <TaskyInput value={form.description ?? ''} maxLength={300} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder={PLACEHOLDERS[kind].description} />
          </label>
          <div className="flex flex-col gap-1 text-sm font-medium">
            {kind === 'talent' ? '顔画像' : '写真'}
            <div className="flex items-center gap-3">
              <span className="block aspect-[3/4] w-20 overflow-hidden rounded-tasky-sm bg-warm-surface">
                {imageUrl && <img src={imageUrl} alt="" className="h-full w-full object-cover" />}
              </span>
              <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-tasky-sm border border-warm-border px-3 py-2 text-sm font-normal hover:bg-warm-hover">
                <ImagePlus className="h-4 w-4" aria-hidden />
                画像を選ぶ
                <input type="file" accept="image/jpeg,image/png,image/webp" className="sr-only" onChange={(e) => void upload(e.target.files?.[0])} data-testid="ugc-preset-file" />
              </label>
            </div>
          </div>
          <label className="flex flex-col gap-1 text-sm font-medium">
            映像の描写（英語推奨・プロンプトに入る）
            <TaskyTextarea rows={3} value={form.prompt_hint ?? ''} maxLength={1000} onChange={(e) => setForm({ ...form, prompt_hint: e.target.value })} placeholder={PLACEHOLDERS[kind].promptHint} />
          </label>
          {kind === 'talent' && (
            <label className="flex flex-col gap-1 text-sm font-medium">
              合わせる声
              <TaskySelect value={form.voice ?? ''} onChange={(e) => setForm({ ...form, voice: e.target.value || null })}>
                <option value="">指定なし</option>
                {UGC_VOICES.map((voice) => (
                  <option key={voice.key} value={voice.key}>
                    {voice.label}（{voice.key}）
                  </option>
                ))}
              </TaskySelect>
            </label>
          )}
          <label className="flex flex-col gap-1 text-sm font-medium">
            並び順（小さいほど先・先頭がおすすめ）
            <TaskyInput type="number" min={0} value={form.sort_order ?? 0} onChange={(e) => setForm({ ...form, sort_order: Number(e.target.value) })} />
          </label>
        </div>
      </TaskyDialog>
    </div>
  );
};

export default UgcPresetPanel;
